import React, { useState, useEffect, useContext, useMemo } from 'react';
import { apiRequest } from '../api';
import { AuthContext } from '../context/AuthContext';
import API_BASE_URL from '../apiConfig';
import './TransactionHistory.css';
import arrowIcon from '../assets/icons/arrow-icon.png';
import starIcon from '../assets/icons/star-icon.png';
import tokenIcon from '../assets/icons/token-yellow.png';
import usdtIcon from '../assets/icons/usdt-icon.png';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const TransactionHistory = ({ isTelegramMiniApp }) => {
  const { user } = useContext(AuthContext);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { t, ready } = useTranslation('transactionHistory');

  const getCurrencyIcon = (currency) => {
    switch (currency) {
      case 'stars':
        return (
          <img
            src={starIcon}
            alt="Stars"
            className="transactions-currency-icon transactions-currency-icon-stars"
          />
        );
      case 'token':
        return (
          <img
            src={tokenIcon}
            alt="Token"
            className="transactions-currency-icon transactions-currency-icon-token"
          />
        );
      default:
        return (
          <img
            src={usdtIcon}
            alt="USDT"
            className="transactions-currency-icon transactions-currency-icon-usdt"
          />
        );
    }
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        const response = await apiRequest(
          `${API_BASE_URL}/api/transactions/${user._id}`,
          {
            method: 'GET',
            credentials: 'include',
          }
        );

        if (response.ok) {
          const data = await response.json();
          setTransactions(data.transactions);
        } else {
          setError('error');
        }
      } catch (err) {
        setError('error');
      } finally {
        setLoading(false);
      }
    };

    if (user && user._id) {
      fetchTransactions();
    }
  }, [user]);

  const filteredTransactions = useMemo(() => {
    return transactions
        .filter(transaction => transaction.status === 'completed')
        .filter(transaction => (
            // Filter by transaction type
            (transaction.type === 'deposit' || 
             transaction.type === 'withdrawal' ||
             transaction.type === 'exchange' || 
             transaction.type === 'claim-reward' || 
             transaction.type === 'task-reward') &&
            // Filter by currency based on platform
            (isTelegramMiniApp 
                ? transaction.currency === 'stars' ||  transaction.currency === 'token' // Only show stars in mini-app
                : transaction.currency === 'usdt' || transaction.currency === 'token') // Don't show stars in web
        ))
        .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [transactions, isTelegramMiniApp]);

  const groupedTransactions = useMemo(() => {
    if (!ready) return {};

    const months = t('months', { returnObjects: true });

    return filteredTransactions.reduce((acc, transaction) => {
      const dateObj = new Date(transaction.date);
      const monthIndex = dateObj.getMonth();
      const monthName = months[monthIndex] || '';
      const dateKey = `${dateObj.getDate()} ${monthName}`;

      const formattedDate = `${String(dateObj.getDate()).padStart(2, '0')}.${String(
        dateObj.getMonth() + 1
      ).padStart(2, '0')}.${dateObj.getFullYear()}, ${String(dateObj.getHours()).padStart(
        2,
        '0'
      )}:${String(dateObj.getMinutes()).padStart(2, '0')}:${String(
        dateObj.getSeconds()
      ).padStart(2, '0')}`;

      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push({ ...transaction, formattedDate });
      return acc;
    }, {});
  }, [filteredTransactions, ready, t]);

  if (loading || !ready) {
    return (
      <div className="transaction-history-container">
        <div className="loading-message">{t('loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="transaction-history-container">
        <div className="error-message">{t('error')}</div>
      </div>
    );
  }

  return (
    <div className="transaction-history-container">
      <div className="history-header">
        {!isTelegramMiniApp && (
          <button
            onClick={() => navigate(-1)}
            className="history-back-button"
          >
            <img
              src={arrowIcon}
              alt="back"
              className="history-back-icon"
            />
          </button>
        )}
        <h2 className="history-page-title">{t('title')}</h2>
      </div>

      <div className="transaction-list">
        {Object.keys(groupedTransactions).length > 0 ? (
          Object.entries(groupedTransactions).map(([date, transactions]) => (
            <div key={date} className="transaction-group">
              <h3 className="transaction-group-date">{date}</h3>
              {transactions.map((transaction) => (
                <div key={transaction._id} className="transaction-item">
                  <div className="transaction-info">
                    <div className="transaction-type">
                      {t(`types.${transaction.type}`, {
                        defaultValue:
                          transaction.type === 'claim-reward'
                            ? 'Reward'
                            : transaction.type.charAt(0).toUpperCase() +
                              transaction.type.slice(1),
                      })}
                    </div>
                    <div className="transaction-date">
                      {transaction.formattedDate}
                    </div>
                  </div>
                  <div className="transaction-amount">
                    <span className="amount-value">
                      {transaction.type === 'deposit' ||
                      transaction.type === 'exchange' ||
                      transaction.type === 'claim-reward' ||
                      transaction.type === 'task-reward'
                        ? '+'
                        : '-'}
                      {transaction.currency === 'usdt'
                        ? transaction.amount.toFixed(2)
                        : transaction.amount}
                    </span>
                    {getCurrencyIcon(transaction.currency || 'usdt')}
                  </div>
                </div>
              ))}
            </div>
          ))
        ) : (
          <div className="empty-message">{t('empty')}</div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;